import PropTypes from 'prop-types';
import styled from 'styled-components';
import React, { useContext } from 'react';
import { Copy, Icon, Optional, Pill } from '@honda-canada/design-system-react';
import { compiler } from 'markdown-to-jsx';
import { stripMarkdownHeading } from '../../../utils/markdown';

import HeaderContext from '../service/HeaderContext';
import RoutableSitecoreLink from '../../RoutableSitecoreLink';
import themeStyles from '../styles/Header.styles';
import { getGtmTagValue } from '../../../utils/gtmEvents';

const Link = styled(RoutableSitecoreLink)(themeStyles.get('ActionLink'));
const ActionIcon = themeStyles.apply(Icon, 'ActionIcon');
const ExternalIcon = themeStyles.apply(Icon, 'ExternalIcon');

export const getDataTrackingLabel = (gtmParentName, parentDisplayName, gtmTitle) => {
  const parts = [];
  if (gtmParentName) parts.push(gtmParentName);
  if (parentDisplayName && parentDisplayName !== gtmTitle) parts.push(parentDisplayName);
  if (gtmTitle) parts.push(getGtmTagValue(gtmTitle));
  return parts.join(' > ') || '';
};

const ActionLink = ({
  copyFontWeight,
  copySize,
  iconColor,
  gtmInteractionType,
  gtmTags,
  gtmTitle,
  gtmParentName,
  gtmItemName,
  href,
  linktype,
  parentDisplayName,
  pill,
  title,
  withIcon,
  querystring,
  ...rest
}) => {
  const isExternal = linktype === 'external';
  const { linkHasExternalIcon, setActiveMenu, navHeaderTitleFont } = useContext(HeaderContext);

  // Close menu groups after clicking links
  const handleSubItemClick = () => setActiveMenu(null);

  // Call the function to get the dataTrackingLabel
  const dataTrackingLabel = getDataTrackingLabel(gtmParentName, parentDisplayName, gtmTitle);

  return (
    <Link
      styling="secondary"
      aria-label={title}
      data-gtm-interaction-type={getGtmTagValue(gtmInteractionType)}
      data-gtm-title={getGtmTagValue(gtmTitle)}
      data-gtm-component-type={getGtmTagValue(gtmTags?.type)}
      data-gtm-nav-type="global"
      data-gtm-header={getGtmTagValue(gtmParentName)}
      data-gtm-label={getGtmTagValue(gtmItemName)}
      data-tracking-label={dataTrackingLabel}
      field={{
        value: {
          linktype,
          href,
          querystring,
          target: '_self',
        },
      }}
      onClick={handleSubItemClick}
      {...rest}
    >
      <Optional when={!!title}>
        <Copy as="span" d="inline" size={copySize} fontWeight={copyFontWeight}>
          {compiler(stripMarkdownHeading(title), {
            namedCodesToUnicode: {
              copy: '\u00A9',
            },
          })}
        </Copy>
      </Optional>
      <Optional when={pill?.pillLabel}>
        <Pill
          ml="xs"
          pillLabel={pill?.pillLabel}
          pillType={pill?.pillType?.toLowerCase()}
          data-testid="header-menu-nav-action-item-pill"
          fontFamily={navHeaderTitleFont}
        />
      </Optional>
      <Optional when={linkHasExternalIcon && isExternal}>
        <ExternalIcon name="externalLink" color={iconColor} />
      </Optional>
      <Optional when={withIcon && !(linkHasExternalIcon && isExternal)}>
        <ActionIcon name="arrowRight" color={iconColor} />
      </Optional>
    </Link>
  );
};

ActionLink.propTypes = {
  title: PropTypes.string,
  withIcon: PropTypes.bool,
  gtmTitle: PropTypes.string,
  gtmInteractionType: PropTypes.string,
  gtmTags: PropTypes.shape({
    type: PropTypes.string,
  }),
  href: PropTypes.string.isRequired,
  querystring: PropTypes.string,
};

export default ActionLink;
